import React from "react";
import { useSnapshot } from "valtio";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { VerticalDotsIcon } from "@aureskonnect/react-ui";

import ErrorToast from "@components/Common/ErrorTost";
import { getUuidUser } from "@helpers/getUuidUser";

import { RootState } from "../../../../../redux/store";
import { store as fidelity } from "../../../../../components/VerticalLayout/store";
import { getArrayOfRows } from "../../helpers";
import { ConfirmationArchive } from "../../ConfirmationArchive";
import { useFetchGlobal } from "../../../../../api/hooks";
import { extractIds, hasDuplicateShops } from "../helpers";

import { ActionColumnProps } from "./types";
import { useUnarchiveBulk } from "./useUnarchiveBulk";
import TabSwitchItems from "./components/TabSwitchItems";
import BulkArchiveMenuItem from "./components/BulkArchiveMenuItem";

export function ActionColumnWrapper({
    selectedRows,
    setDataIsUpdated,
    setIsFidelityArchived,
    isFidelityArchived,
    setLimit,
}: ActionColumnProps) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const archivedFidelityFranchise = useSelector(
        (s: RootState) => s.fidelity.archivedFidelityFranchise
    );
    const modify = useSelector((s: RootState) => s.fidelity.modify);
    const isSupportCodeValid = useSelector(
        (s: RootState) => s.fidelity.isSupportCodeValid
    );
    const fidelityData = useSelector((s: RootState) => s.fidelity.fidelityData);
    const { franchiseID, userID, operatorID } = useSnapshot(fidelity);
    const uuidUser = getUuidUser();

    const [isFidelityClicked, setIsFidelityClicked] = React.useState(false);
    const [isArchiveModalOpened, setIsArchiveModalOpened] = React.useState(
        false
    );

    const arrayOfRows = getArrayOfRows(selectedRows, t);
    const archivedFranchiseIds = extractIds(archivedFidelityFranchise);
    const dataArchive = extractIds(fidelityData);
    const hasDuplicates = hasDuplicateShops(arrayOfRows);

    const unarchive = useUnarchiveBulk({
        arrayOfRows,
        userID,
        franchiseID,
        operatorID,
        uuidUser,
        fetchGlobal,
        setDataIsUpdated,
        t,
    });

    const isDisabled =
        selectedRows.length === 0 ||
        (isFidelityArchived &&
            arrayOfRows.some((el: any) =>
                archivedFranchiseIds.includes(el.Id)
            )) ||
        (isFidelityArchived &&
            arrayOfRows.some((el: any) => dataArchive.includes(el.Id))) ||
        arrayOfRows.length === 0 ||
        arrayOfRows.every((el: any) => el[t("State")] === true) ||
        (isFidelityArchived && hasDuplicates);

    const handleArchiveClick = () => {
        if (!isFidelityArchived) {
            ErrorToast(
                `${t(
                    "Please note, loyalty archiving is done exclusively by the franchise."
                )!}`
            );
            return;
        }
        setIsFidelityClicked(true);
        if (arrayOfRows.length < 2) {
            ErrorToast(
                `${t(
                    "Attention, it is a multiple action. Please select at least two items"
                )!}`
            );
        } else {
            unarchive();
        }
    };

    const showConfirmArchive =
        isArchiveModalOpened || (isFidelityClicked && isSupportCodeValid);

    return (
        <React.Fragment>
            <div>
                <div className="dropdown">
                    <VerticalDotsIcon
                        id="dropdownMenuButton1"
                        className="cmn_drp_BTmenu"
                        data-bs-toggle="dropdown"
                        style={{ cursor: "pointer" }}
                        height={25}
                        width={25}
                        fill="black"
                    />
                    <div
                        className="dropdown-menu"
                        aria-labelledby="dropdownMenuButton"
                    >
                        <TabSwitchItems
                            setIsFidelityArchived={setIsFidelityArchived}
                            t={t}
                        />
                        {modify === true ? (
                            <BulkArchiveMenuItem
                                isDisabled={isDisabled}
                                isFidelityArchived={isFidelityArchived}
                                onClick={handleArchiveClick}
                                t={t}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
            {showConfirmArchive ? (
                <ConfirmationArchive
                    isModalOpened={showConfirmArchive}
                    setIsModalOpened={setIsArchiveModalOpened}
                    setDataIsUpdated={setDataIsUpdated}
                    selectedRows={selectedRows}
                    setLimit={setLimit}
                />
            ) : null}
        </React.Fragment>
    );
}
